// # Mentee Meetings Component for Step 2 Mentorship
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Tabs, Tab, TabList, TabPanel } from 'react-tabs';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { FaList, FaCalendarAlt, FaExternalLinkAlt, FaSpinner, FaCrown, FaExclamationTriangle } from 'react-icons/fa';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuthStore } from '../../../auth/state';
import SharedHeader from '../../../components/SharedHeader';
import DashboardFooter from '../../../components/DashboardFooter';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import './mentee_meetings2.css';

// # Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="mentee_meetings2_error">
          <FaExclamationTriangle />
          <span>Something went wrong. Please try refreshing the page.</span>
        </div>
      );
    }
    return this.props.children;
  }
}

function MenteeMeetings2({ isAdmin, isMentorView }) {
  const navigate = useNavigate();
  const { username } = useParams(); // # Get mentee username from URL if in mentor view
  const { userProfile, user } = useAuthStore();
  const [activeTab, setActiveTab] = useState(0);
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [hostData, setHostData] = useState({});
  const [calendarKey, setCalendarKey] = useState(0);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [calendarView, setCalendarView] = useState('dayGridMonth');
  const [targetUserId, setTargetUserId] = useState(null);
  const [isMentor, setIsMentor] = useState(false);
  const [targetUserData, setTargetUserData] = useState(null);
  
  // # Create base path based on admin status
  const basePath = isAdmin ? '/admin/mentorship' : '/mentorship';

  // # Get the correct user ID based on context (mentor view or regular view)
  useEffect(() => {
    const getMenteeId = async () => {
      // # If we're in mentor view and have a username param, get the mentee's ID
      if ((isMentorView || isAdmin) && username) {
        try {
          // # Look up the mentee's user ID from their username by querying the users collection
          const usersCollection = collection(db, 'users');
          const q = query(usersCollection, where('username', '==', username));
          const querySnapshot = await getDocs(q);
          
          if (!querySnapshot.empty) {
            const userData = querySnapshot.docs[0].data();
            const menteeId = querySnapshot.docs[0].id;
            setTargetUserId(menteeId);
            setIsMentor(true);
            
            // # Store in session for other components to use
            sessionStorage.setItem('currentViewingMenteeId', menteeId);
            
            // # Set mentee user data
            setTargetUserData(userData);
          } else {
            setError(`Mentee with username "${username}" not found.`);
            setLoading(false);
          }
        } catch (err) {
          console.error('Error fetching mentee ID:', err);
          setError('Failed to load mentee data. Please try again later.');
          setLoading(false);
        }
      } else {
        // # Regular mentee view - use current user ID
        setTargetUserId(user?.uid);
        setIsMentor(false);
      }
    };
    
    getMenteeId();
    
    return () => {
      // # Clean up session storage when component unmounts
      if (isMentorView || isAdmin) {
        sessionStorage.removeItem('currentViewingMenteeId');
      }
    };
  }, [user?.uid, username, isMentorView, isAdmin]);

  // # Memoized tab configuration
  const tabs = useMemo(() => [
    {
      label: 'Meeting List',
      icon: <FaList className="mentee_meetings2_tab_icon" />,
    },
    {
      label: 'Calendar View',
      icon: <FaCalendarAlt className="mentee_meetings2_tab_icon" />,
    }
  ], []);

  // # Optimized user data fetching
  const fetchUserData = useCallback(async (meetings) => {
    const uniqueHostIds = [...new Set(meetings.map(meeting => meeting.host.uid))];
    
    try {
      const userDataPromises = uniqueHostIds.map(async (uid) => {
        const userDoc = await getDoc(doc(db, 'users', uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          return [uid, {
            displayName: `${data.firstName || ''} ${data.lastName || ''}`.trim() || data.username || 'Unknown',
            email: data.email || 'No email',
            avatar: data.photoURL || null,
            username: data.username || null
          }];
        }
        return [uid, { displayName: 'Unknown User', email: 'No email', avatar: null }];
      });

      const userDataResults = await Promise.all(userDataPromises);
      setHostData(Object.fromEntries(userDataResults));
    } catch (error) {
      console.error('Error fetching user data:', error);
      setError('Failed to load user information. Some host details may be missing.');
    }
  }, []);

  // # Fetch meetings specific to Step 2 mentorship
  const fetchMeetings = useCallback(async () => {
    if (!targetUserId) return;

    setLoading(true);
    setError('');
    
    try {
      // # Specifically get only step2_meetings
      const meetingsRef = doc(db, 'meetings', 'step2_meetings');
      const docSnap = await getDoc(meetingsRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        // # Safely handle different document structures
        const filteredMeetings = Object.entries(data)
          .filter(([_, meeting]) => {
            // # Skip entries that don't have proper structure
            if (!meeting || typeof meeting !== 'object') return false;
            
            // # Check if invited property exists and includes the target user
            try {
              return Array.isArray(meeting.invited) && meeting.invited.includes(targetUserId);
            } catch (err) {
              console.warn('Error checking invited property for a meeting:', err);
              return false;
            }
          })
          .map(([id, meeting]) => {
            try {
              return {
                id,
                type: 'step2_meetings',
                ...meeting,
                // # Safely convert meet_time to Date - handle different formats
                meet_time: meeting.meet_time instanceof Date 
                  ? meeting.meet_time 
                  : typeof meeting.meet_time === 'string' 
                    ? new Date(meeting.meet_time) 
                    : meeting.meet_time && typeof meeting.meet_time.toDate === 'function'
                      ? meeting.meet_time.toDate()
                      : new Date() // Fallback to current date if invalid
              };
            } catch (err) {
              console.warn('Error processing meeting data:', err);
              return null;
            }
          })
          .filter(Boolean); // Remove any null entries from errors
        
        // # Sort meetings by time (upcoming first)
        filteredMeetings.sort((a, b) => a.meet_time - b.meet_time);
        setMeetings(filteredMeetings);
        
        // # Fetch host data
        if (filteredMeetings.length > 0) {
          await fetchUserData(filteredMeetings);
        }
      } else {
        setMeetings([]);
      }
    } catch (error) {
      console.error('Error fetching meetings:', error);
      setError('Failed to load meetings. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [targetUserId, fetchUserData]);

  // # Initial meetings fetch
  useEffect(() => {
    if (targetUserId) {
      fetchMeetings();
    }
    
    // # Handle window resize
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      // # Adjust calendar view based on screen size
      if (window.innerWidth < 768) {
        setCalendarView('dayGridDay');
      } else {
        setCalendarView('dayGridMonth');
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // Initial check
    
    return () => window.removeEventListener('resize', handleResize);
  }, [fetchMeetings, targetUserId]);

  // # Memoized filtered meetings
  const filteredMeetings = useMemo(() => {
    const searchLower = searchQuery.toLowerCase();
    return meetings.filter(meeting => {
      const host = hostData[meeting.host.uid] || {};
      return (
        meeting.title.toLowerCase().includes(searchLower) ||
        meeting.description.toLowerCase().includes(searchLower) ||
        host.displayName?.toLowerCase().includes(searchLower) ||
        host.email?.toLowerCase().includes(searchLower)
      );
    });
  }, [meetings, searchQuery, hostData]);

  // # Group meetings into sections
  const groupedMeetings = useMemo(() => {
    const now = new Date();
    const within24Hours = [];
    const within7Days = [];
    const otherMeetings = [];

    filteredMeetings.forEach(meeting => {
      const meetingTime = new Date(meeting.meet_time);
      const hoursUntilMeeting = (meetingTime - now) / (1000 * 60 * 60);
      const daysUntilMeeting = hoursUntilMeeting / 24;
      
      if (hoursUntilMeeting < 0) {
        otherMeetings.push(meeting);
        return;
      }
      
      if (hoursUntilMeeting <= 24) {
        within24Hours.push(meeting);
      } else if (daysUntilMeeting <= 7) {
        within7Days.push(meeting);
      } else {
        otherMeetings.push(meeting);
      }
    });
    
    // # Sort each section by meeting time
    const sortByTime = (a, b) => new Date(a.meet_time) - new Date(b.meet_time);
    within24Hours.sort(sortByTime);
    within7Days.sort(sortByTime);
    otherMeetings.sort(sortByTime);
    
    return {
      within24Hours,
      within7Days,
      otherMeetings
    };
  }, [filteredMeetings]);

  // # Memoized calendar events
  const events = useMemo(() => meetings.map(meeting => {
    // # Calculate time until meeting
    const now = new Date();
    const meetingTime = new Date(meeting.meet_time);
    const hoursUntilMeeting = (meetingTime - now) / (1000 * 60 * 60);

    // # Determine event class based on time
    let eventClass = '';
    if (hoursUntilMeeting < -1) {
      eventClass = 'past-meeting';
    } else if (hoursUntilMeeting <= 1 && hoursUntilMeeting > -1) {
      eventClass = 'joinable';
    } else if (hoursUntilMeeting <= 12 && hoursUntilMeeting > 1) {
      eventClass = 'upcoming-soon';
    }

    return {
      id: meeting.id,
      title: meeting.title,
      start: meeting.meet_time,
      end: new Date(new Date(meeting.meet_time).getTime() + 3600000), // # 1 hour duration
      hostData: hostData[meeting.host.uid] || {
        displayName: 'Loading...',
        email: '',
        avatar: null,
        username: null
      },
      className: eventClass,
      display: 'block'
    };
  }), [meetings, hostData]);

  // # Format date and time helper
  const formatDateTime = useCallback((dateString) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString('en-US', { 
        weekday: 'short', 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      }),
      time: date.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit'
      })
    };
  }, []);

  // # Optimized calendar options with responsive handling
  const calendarOptions = useMemo(() => ({
    plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
    initialView: calendarView,
    headerToolbar: windowWidth < 768 ? {
      left: 'prev,next',
      center: 'title',
      right: 'dayGridDay,dayGridWeek'
    } : {
      left: 'prev,next today',
      center: 'title',
      right: 'dayGridMonth,timeGridWeek,timeGridDay'
    },
    events,
    editable: false,
    selectable: true,
    selectMirror: true,
    dayMaxEvents: windowWidth < 768 ? 2 : true,
    height: '100%',
    aspectRatio: windowWidth < 768 ? 0.8 : 1.35,
    eventTimeFormat: {
      hour: '2-digit',
      minute: '2-digit',
      meridiem: 'short'
    },
    eventContent: (eventInfo) => {
      const event = eventInfo.event;
      const hostData = event.extendedProps.hostData;
      const hostName = hostData?.displayName || 'Loading...';
      const isPast = new Date(event.start) < new Date();
      
      // # Simplified event content for mobile
      if (windowWidth < 768) {
        return {
          html: `
            <div class="mentee_meetings2_calendar_event ${isPast ? 'past-meeting' : ''}">
              <div class="mentee_meetings2_calendar_event_title text-ellipsis" title="${event.title}">
                ${event.title}
              </div>
            </div>
          `
        };
      }
      
      return {
        html: `
          <div class="mentee_meetings2_calendar_event ${isPast ? 'past-meeting' : ''}">
            <div class="mentee_meetings2_calendar_event_title text-ellipsis" title="${event.title}">
              ${event.title}
            </div>
            <div class="mentee_meetings2_calendar_event_host text-ellipsis" title="Host: ${hostName}">
              <span class="mentee_meetings2_calendar_host_icon">👑</span>
              ${hostName}
            </div>
            <div class="mentee_meetings2_calendar_event_time text-ellipsis">
              <span class="mentee_meetings2_calendar_time_icon">🕒</span>
              ${event.start.toLocaleTimeString([], { 
                hour: '2-digit', 
                minute: '2-digit',
                hour12: true 
              })}
            </div>
          </div>
        `
      };
    },
    slotMinTime: '06:00:00',
    slotMaxTime: '22:00:00',
    allDaySlot: false,
    nowIndicator: true,
    weekNumbers: windowWidth >= 768,
    weekNumberFormat: { week: 'numeric' },
    dayCellClassNames: 'mentee_meetings2_calendar_cell',
    handleWindowResize: true,
    contentHeight: 'auto',
    expandRows: false,
    fixedWeekCount: false,
    stickyHeaderDates: false,
    stickyFooterScrollbar: false,
    dayMinWidth: null,
    dayMaxEventRows: windowWidth < 768 ? 2 : 6,
    views: {
      dayGrid: {
        dayMaxEventRows: windowWidth < 768 ? 2 : 6
      },
      timeGrid: {
        dayMaxEventRows: windowWidth < 768 ? 2 : 6
      }
    }
  }), [events, windowWidth, calendarView]);

  // # Optimized meeting item rendering with truncation
  const renderMeetingItem = useCallback((meeting) => {
    const { date, time } = formatDateTime(meeting.meet_time);
    const host = hostData[meeting.host.uid] || {};
    
    // # Calculate time until meeting
    const now = new Date();
    const meetingTime = new Date(meeting.meet_time);
    const hoursUntilMeeting = (meetingTime - now) / (1000 * 60 * 60);
    
    // # Determine meeting states
    const isPastMeeting = hoursUntilMeeting < -1;
    const isJoinable = hoursUntilMeeting <= 1 && hoursUntilMeeting > -1;
    const isUpcoming = hoursUntilMeeting <= 12 && hoursUntilMeeting > 1;
    
    // # Determine item class based on time
    let itemClass = 'mentee_meetings2_item';
    if (isPastMeeting) {
      itemClass += ' past-meeting';
    } else if (isJoinable) {
      itemClass += ' joinable';
    } else if (isUpcoming) {
      itemClass += ' upcoming-soon';
    }
    
    // # Determine join button state
    const joinButtonClass = `mentee_meetings2_action_btn join ${isJoinable ? 'active' : ''} ${isUpcoming ? 'upcoming' : ''}`;
    
    return (
      <div key={meeting.id} className={itemClass}>
        <div className="mentee_meetings2_item_header">
          <h3 title={meeting.title}>{meeting.title}</h3>
          {!isPastMeeting && (
            <div className="mentee_meetings2_item_actions">
              <a 
                href={meeting.host.url}
                target="_blank"
                rel="noopener noreferrer"
                className={joinButtonClass}
                title={isJoinable ? "Join Meeting" : isUpcoming ? "Meeting starts soon" : "Meeting not yet available"}
                onClick={(e) => !isJoinable && e.preventDefault()}
              >
                <FaExternalLinkAlt />
                <span className="sr-only">
                  {isJoinable ? "Join Meeting" : isUpcoming ? "Meeting starts soon" : "Meeting not yet available"}
                </span>
              </a>
            </div>
          )}
        </div>
        <p className="mentee_meetings2_item_desc" title={meeting.description}>
          {meeting.description}
        </p>
        <div className="mentee_meetings2_host_info">
          {host.avatar ? (
            <img 
              src={host.avatar} 
              alt={host.displayName} 
              className="mentee_meetings2_host_avatar"
              loading="lazy"
            />
          ) : (
            <div className="mentee_meetings2_host_avatar_placeholder">
              {host.displayName?.charAt(0) || '?'}
            </div>
          )}
          <div className="mentee_meetings2_host_details">
            <div className="mentee_meetings2_host_name_container">
              <span className="mentee_meetings2_host_name text-ellipsis" title={host.displayName}>
                {host.displayName}
              </span>
              <span className="mentee_meetings2_host_badge">
                <FaCrown /> Mentor
              </span>
            </div>
            <span className="mentee_meetings2_host_email text-ellipsis" title={host.email}>
              {host.email}
            </span>
          </div>
        </div>
        <div className="mentee_meetings2_item_details">
          <span className="mentee_meetings2_item_date text-ellipsis" title={`${date} at ${time}`}>
            <FaCalendarAlt /> {date} at {time}
          </span>
        </div>
      </div>
    );
  }, [formatDateTime, hostData]);

  // # Render a section of meetings
  const renderMeetingsSection = useCallback((title, meetings, iconClass) => {
    if (meetings.length === 0) return null;
    
    return (
      <div className="mentee_meetings2_section">
        <div className="mentee_meetings2_section_header">
          <h2>
            <span className={`mentee_meetings2_section_icon ${iconClass}`}></span>
            {title}
            <span className="mentee_meetings2_count">{meetings.length}</span>
          </h2>
        </div>
        <div className="mentee_meetings2_items_container">
          {meetings.map(renderMeetingItem)}
        </div>
      </div>
    );
  }, [renderMeetingItem]);

  // # Get title based on context
  const getPageTitle = () => {
    if (isMentor) {
      const menteeName = targetUserData ? 
        `${targetUserData.firstName || ''} ${targetUserData.lastName || ''}`.trim() || 
        targetUserData.username : 
        username;
      return `${menteeName}'s Step 2 Meetings`;
    }
    return 'Step 2 Mentee Meetings';
  };

  return (
    <ErrorBoundary>
    <div className="mentee_meetings2_container">
      <SharedHeader />
        
      <div className="mentee_meetings2_main">
        <div className="mentee_meetings2_content">
            <div className="mentee_meetings2_header">
              <div className="mentee_meetings2_title_box">
                <h1>{getPageTitle()}</h1>
                {isMentor && (
                  <div className="mentee_meetings2_mentor_indicator">
                    Mentor View
                  </div>
                )}
                <div className="mentee_meetings2_title_underline"></div>
              </div>
            </div>

            {error && (
              <div className="mentee_meetings2_error" role="alert">
                <FaExclamationTriangle />
                <span>{error}</span>
              </div>
            )}

            {loading ? (
              <div className="mentee_meetings2_loading">
                <FaSpinner className="icon-spin" />
                <span>Loading meetings...</span>
              </div>
            ) : (
              <div className="mentee_meetings2_tabs">
                <Tabs
                  selectedIndex={activeTab}
                  onSelect={setActiveTab}
                  selectedTabClassName="mentee_meetings2_tab--selected"
                >
                  <TabList className="mentee_meetings2_tab_list">
                    {tabs.map((tab, index) => (
                      <Tab 
                        key={index}
                        className={`mentee_meetings2_tab ${activeTab === index ? 'active' : ''}`}
                      >
                        {tab.icon}
                        <span className="mentee_meetings2_tab_label">
                          {tab.label}
                          {activeTab === index && (
                            <span className="mentee_meetings2_tab_active_indicator" />
                          )}
                        </span>
                      </Tab>
                    ))}
                  </TabList>

                  <TabPanel className={`mentee_meetings2_tab_panel ${activeTab === 0 ? 'active' : ''}`}>
                    <div className="mentee_meetings2_list_container">
                      <div className="mentee_meetings2_search">
                        <input
                          type="text"
                          placeholder={windowWidth < 768 ? "Search meetings..." : "Search meetings by title, description, or mentor..."}
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="mentee_meetings2_search_input"
                        />
                      </div>
                      <div className="mentee_meetings2_list">
                        {filteredMeetings.length > 0 ? (
                          <>
                            {renderMeetingsSection(
                              "Meetings in 24 Hours", 
                              groupedMeetings.within24Hours,
                              "upcoming"
                            )}
                            
                            {renderMeetingsSection(
                              "Meetings in 7 Days", 
                              groupedMeetings.within7Days,
                              "week"
                            )}
                            
                            {renderMeetingsSection(
                              "Other Meetings", 
                              groupedMeetings.otherMeetings,
                              "other"
                            )}
                          </>
                        ) : (
                          <div className="mentee_meetings2_empty">
                            <FaCalendarAlt className="mentee_meetings2_empty_icon" />
                            <p>No meetings found</p>
                            {searchQuery && <p>Try adjusting your search criteria</p>}
                            {!searchQuery && (
                              <p>
                                {isMentor 
                                  ? "This mentee doesn't have any scheduled meetings yet" 
                                  : "You don't have any scheduled meetings yet"}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </TabPanel>

                  <TabPanel className={`mentee_meetings2_tab_panel ${activeTab === 1 ? 'active' : ''}`}>
                    <div className="mentee_meetings2_calendar_container">
                      <div className="mentee_meetings2_calendar">
                        <FullCalendar
                          key={`${calendarKey}-${windowWidth}`}
                          {...calendarOptions}
                        />
                      </div>
                    </div>
                  </TabPanel>
                </Tabs>
              </div>
            )}
          </div>
        </div>
        
        <DashboardFooter />
      </div>
    </ErrorBoundary>
  );
}

export default MenteeMeetings2;
